<mat-card>
  <div class="header">
    <img src="assets/logo.png" alt="Company Logo" class="logo" />
    <h1>{{ translations.customerOnBoarding }}</h1>
  </div>

  <form [formGroup]="customerForm" class="form">
    @if (!formSubmitted) {
      <div>
        <!-- Step 1: Company Details -->
        @if (step === FormStep.CompanyDetails) {
          <div>
            <h2>{{ translations.companyDetails }}</h2>
            <div class="form-row">
              <mat-form-field
                appearance="outline"
                class="address-full capitalize-first"
              >
                <mat-label>{{ translations.companyName }}</mat-label>
                <input
                  matInput
                  formControlName="name"
                  [placeholder]="translations.companyNamePlaceholder"
                  capitalizeInput
                  required
                  (input)="onInputWithSpace($event, 'name')"
                />
                @if (
                  customerForm.get("name")?.invalid &&
                  customerForm.get("name")?.touched
                ) {
                  <mat-error>
                    @if (customerForm.get("name")?.errors?.["required"]) {
                      {{ translations.companyNameRequired }}
                    }
                    @if (customerForm.get("name")?.errors?.["duplicate"]) {
                      {{ translations.companyNameDuplicate }}
                    }
                  </mat-error>
                }
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>{{ translations.billingEmail }}</mat-label>
                <input
                  matInput
                  type="email"
                  formControlName="billingEmail"
                  [placeholder]="translations.billingEmailPlaceholder"
                  required
                  capitalizeInput
                  (input)="onInputWithSpace($event, 'billingEmail')"
                />
                @if (
                  customerForm.get("billingEmail")?.invalid &&
                  customerForm.get("billingEmail")?.touched
                ) {
                  <mat-error>
                    {{ translations.billingEmailRequired }}
                  </mat-error>
                }
              </mat-form-field>
            </div>

            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>{{ translations.taxId }}</mat-label>
                <input
                  matInput
                  formControlName="taxId"
                  [placeholder]="translations.taxIdPlaceholder"
                  capitalizeInput
                  (input)="onInputWithSpace($event, 'taxId')"
                />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>{{ translations.taxIdType }}</mat-label>
                <mat-select
                  formControlName="taxIdType"
                  [placeholder]="translations.taxIdTypePlaceholder"
                >
                  @for (type of taxIdTypes; track type) {
                    <mat-option [value]="type.value">
                      {{ type.value }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>

            <div class="form-row">
              <mat-form-field appearance="outline">
                <mat-label>{{ translations.businessNumber }}</mat-label>
                <input
                  matInput
                  type="text"
                  formControlName="businessNumber"
                  [placeholder]="translations.businessNumberPlaceholder"
                  required
                  capitalizeInput
                  maxlength="15"
                  (keydown)="allowDigitsPlusSpace($event)"
                />
                @if (
                  customerForm.get("businessNumber")?.invalid &&
                  customerForm.get("businessNumber")?.touched
                ) {
                  <mat-error>
                    {{ translations.businessNumberRequired }}
                  </mat-error>
                }
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>{{ translations.language }}</mat-label>
                <mat-select
                  formControlName="language"
                  required
                  [placeholder]="translations.languagePlaceholder"
                >
                  <mat-option value="English">{{
                    translations.languages.english
                  }}</mat-option>
                  <mat-option value="SimplifiedChinese">{{
                    translations.languages.chinese
                  }}</mat-option>
                </mat-select>
                @if (
                  customerForm.get("language")?.invalid &&
                  customerForm.get("language")?.touched
                ) {
                  <mat-error>
                    {{ translations.languageRequired }}
                  </mat-error>
                }
              </mat-form-field>
            </div>

            <h3>{{ translations.address }}</h3>
            <div>
              <!-- Row 1: Street Address -->
              <div class="address-row">
                <mat-form-field
                  appearance="outline"
                  class="address-full capitalize-first"
                >
                  <mat-label>{{ translations.street }}</mat-label>
                  <input
                    matInput
                    formControlName="streetAddress"
                    [placeholder]="translations.streetPlaceholder"
                    required capitalizeInput
                    (input)="onInputWithSpace($event, 'streetAddress')"
                  />
                  @if (
                    customerForm.get("streetAddress")?.invalid &&
                    customerForm.get("streetAddress")?.touched
                  ) {
                    <mat-error>
                      {{ translations.streetRequired }}
                    </mat-error>
                  }
                </mat-form-field>
                <mat-form-field appearance="outline" class="address-full capitalize-first">
                  <mat-label>{{ translations.street2}}</mat-label>
                  <input matInput formControlName="streetAddress2" [placeholder]="translations.street2Placeholder"
                    capitalizeInput (input)="onInputWithSpace($event, 'streetAddress2')" />
                </mat-form-field>                
              </div>
              <!-- Row 2: State and Country -->
              <div class="address-row">
                <mat-form-field appearance="outline" class="address-half">
                  <mat-label>{{ translations.country }}</mat-label>
                  <mat-select
                    formControlName="country"
                    (selectionChange)="onCountryChange($event)"
                    [placeholder]="translations.countryPlaceholder"
                    required
                  >
                    @for (country of countries; track country) {
                      <mat-option [value]="country.name">
                        {{ country.name }}
                      </mat-option>
                    }
                  </mat-select>
                  @if (
                    customerForm.get("country")?.invalid &&
                    customerForm.get("country")?.touched
                  ) {
                    <mat-error>
                      {{ translations.countryRequired }}
                    </mat-error>
                  }
                </mat-form-field>
                <mat-form-field appearance="outline" class="address-half">
                  <mat-label>{{ translations.state }}</mat-label>
                  <mat-select
                    formControlName="state"
                    (selectionChange)="onStateChange($event)"
                    [placeholder]="translations.statePlaceholder"
                    required
                  >
                    @for (state of states; track state) {
                      <mat-option [value]="state">
                        {{ state.name }}
                      </mat-option>
                    }
                  </mat-select>
                  @if (
                    customerForm.get("state")?.invalid &&
                    customerForm.get("state")?.touched
                  ) {
                    <mat-error>
                      {{ translations.stateRequired }}
                    </mat-error>
                  }
                </mat-form-field>
              </div>
              <!-- Row 3: Suburb, City, Postcode -->
              <div class="address-row">
                <mat-form-field
                  appearance="outline"
                  class="address-third capitalize-first"
                >
                  <mat-label>{{ translations.suburb }} </mat-label>
                  <input
                    matInput
                    formControlName="suburb"
                    [placeholder]="
                      translations.suburbPlaceholder || 'Enter suburb'
                    "
                    required
                    capitalizeInput
                    (input)="onInputWithSpace($event, 'suburb')"
                  />
                  @if (
                    customerForm.get("suburb")?.invalid &&
                    customerForm.get("suburb")?.touched
                  ) {
                    <mat-error>
                      {{ translations.suburbRequired }}
                    </mat-error>
                  }
                </mat-form-field>
                @if (
                  customerForm.get("country")?.value === "India" ||
                  customerForm.get("country")?.value === "China"
                ) {
                  <mat-form-field
                    appearance="outline"
                    class="address-third capitalize-first"
                  >
                    <mat-label>{{ translations.city }}</mat-label>
                    <input
                      matInput
                      formControlName="city"
                      [placeholder]="
                        translations.cityPlaceholder || 'Enter city name'
                      "
                      capitalizeInput
                      (input)="onInputWithSpace($event, 'city')"
                    />
                  </mat-form-field>
                }
                <mat-form-field appearance="outline" class="address-third">
                  <mat-label>{{ translations.postcode }}</mat-label>
                  <input
                    matInput
                    formControlName="postCode"
                    [placeholder]="
                      translations.postcodePlaceholder || 'Enter post code'
                    "
                    required
                    capitalizeInput
                    (keydown.space)="$event.preventDefault()"
                  />
                  @if (
                    customerForm.get("postCode")?.invalid &&
                    customerForm.get("postCode")?.touched
                  ) {
                    <mat-error>
                      {{ translations.postcodeRequired }}
                    </mat-error>
                  }
                </mat-form-field>
              </div>
            </div>
            <!-- Step 1: Company Details button row -->
            <div class="button-row">
              <button
                mat-raised-button
                color="primary"
                (click)="nextStep()"
                [disabled]="!canProceedToNextStep()"
                class="nav-next-btn"
              >
                {{ translations.next }}
              </button>
            </div>
          </div>
        }
        <!-- Step 3: User Accounts -->
        @if (step === FormStep.UserAccounts) {
          <div>
            <h2>{{ translations.userAccounts }}</h2>
            <!-- Add User Button at the top -->
            <!-- Add User Button at the top -->
            <!-- User List -->
            <div formArrayName="userList">
              @for (user of usersArray.controls; track user; let i = $index) {
                <div [formGroupName]="i" class="user-section">
                  <div class="user-section-header">
                    <h3>
                      {{ translations.userTitle }}
                      {{ i + 1 }}
                    </h3>
                    @if (usersArray.length > 1) {
                      <button
                        type="button"
                        (click)="removeUser(i)"
                        class="remove-user-button"
                        matTooltip="{{ translations.deleteUserButton }}"
                      >
                        <mat-icon style="color: red">delete</mat-icon>
                      </button>
                    }
                  </div>
                  <div class="form-row">
                    <mat-form-field appearance="outline">
                      <mat-label>{{ translations.username }}</mat-label>
                      <input
                        matInput
                        formControlName="username"
                        capitalizeInput
                        (keydown.space)="preventSpace($event)"
                        (input)="user.get('username')?.markAsTouched()"
                      />
                      @if (
                        user.get("username")?.hasError("required") &&
                        user.get("username")?.touched
                      ) {
                        <mat-error>
                          {{ translations.usernameRequired }}
                        </mat-error>
                      }
                      @if (
                        user.get("username")?.hasError("minlength") &&
                        user.get("username")?.touched
                      ) {
                        <mat-error>
                          {{ translations.usernameMinLength }}
                        </mat-error>
                      }
                      @if (
                        user.get("username")?.hasError("duplicateUsername") &&
                        user.get("username")?.touched
                      ) {
                        <mat-error>
                          {{ translations.userNameAlreadyExists }}
                        </mat-error>
                      }
                    </mat-form-field>
                    <mat-form-field
                      appearance="outline"
                      class="capitalize-first"
                    >
                      <mat-label>{{ translations.firstName }}</mat-label>
                      <input
                        matInput
                        formControlName="firstName"
                        capitalizeInput
                        (input)="onInputWithSpaceNew($event, 'firstName', i)"
                      />
                      @if (
                        user.get("firstName")?.hasError("required") &&
                        user.get("firstName")?.touched
                      ) {
                        <mat-error>
                          {{ translations.firstNameRequired }}
                        </mat-error>
                      }
                      @if (
                        user.get("firstName")?.hasError("minlength") &&
                        user.get("firstName")?.touched
                      ) {
                        <mat-error>
                          {{ translations.firstNameMinLength }}
                        </mat-error>
                      }
                    </mat-form-field>
                    <!-- <mat-form-field appearance="outline">
                      <mat-label>{{ translations.businessNumber }}</mat-label>
                      This is displayed as Business Phone Number
                      <input
                        matInput
                        type="email"
                        formControlName="businessNumber"
                        [placeholder]="translations.businessNumberPlaceholder"
                        required
                        capitalizeInput
                        (input)="onInputWithSpace($event, 'businessNumber')"
                      />
                      @if (
                        customerForm.get("businessNumber")?.invalid &&
                        customerForm.get("businessNumber")?.touched
                      ) {
                        <mat-error>
                          {{ translations.businessNumberRequired }}
                        </mat-error>
                      }
                    </mat-form-field> -->
                  </div>
                  <div class="form-row">
                    <mat-form-field
                      appearance="outline"
                      class="capitalize-first"
                    >
                      <mat-label>{{ translations.lastName }}</mat-label>
                      <input
                        matInput
                        formControlName="lastName"
                        capitalizeInput
                        (input)="onInputWithSpaceNew($event, 'lastName', i)"
                      />
                      @if (
                        user.get("lastName")?.invalid &&
                        user.get("lastName")?.touched
                      ) {
                        <mat-error>
                          {{ translations.lastNameRequired }}
                        </mat-error>
                      }
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>{{ translations.email }}</mat-label>
                      <input
                        matInput
                        type="email"
                        formControlName="email"
                        required
                        capitalizeInput
                        (keydown.space)="preventSpace($event)"
                      />
                      @if (
                        user.get("email")?.invalid && user.get("email")?.touched
                      ) {
                        <mat-error>
                          {{ translations.emailRequired }}
                        </mat-error>
                      }
                    </mat-form-field>
                  </div>
                  <div class="form-row">
                    <mat-form-field appearance="outline">
                      <mat-label>{{ translations.mobileNumber }}</mat-label>
                      <input
                        matInput
                        formControlName="mobile"
                        required
                        maxlength="15"
                        [placeholder]="translations.mobileNumberPlaceholder || 'Enter mobile number' "
                        capitalizeInput
                        (keydown)="allowDigitsPlusSpace($event)"
                      />
                      @if (
                        user.get("mobile")?.invalid &&
                        user.get("mobile")?.touched
                      ) {
                        <mat-error>
                          {{ translations.mobileRequired }}
                        </mat-error>
                      }
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>{{ translations.language }} </mat-label>
                      <mat-select
                        formControlName="language"
                        required
                        [placeholder]="translations.languagePlaceholder || ''"
                      >
                        <mat-option value="English">
                          {{ translations.languages.english }}
                        </mat-option>
                        <mat-option value="SimplifiedChinese">
                          {{ translations.languages.chinese }}
                        </mat-option>
                      </mat-select>
                      @if (
                        user.get("language")?.invalid &&
                        user.get("language")?.touched
                      ) {
                        <mat-error>
                          {{ translations.languageRequired }}
                        </mat-error>
                      }
                    </mat-form-field>
                  </div>
                  <div class="form-row software-wechat-row">
                    <mat-form-field
                      appearance="outline"
                      class="software-select-field"
                    >
                      <mat-label>{{ translations.software }}</mat-label>
                      <mat-select
                        formControlName="software"
                        [placeholder]="translations.softwarePlaceholder || ''"
                        (selectionChange)="onSoftwareChange(i)"
                      >
                        @for (
                          software of socialMediaSoftwareOptions;
                          track software
                        ) {
                          <mat-option [value]="software">{{
                            software === "weChatId"
                              ? translations.weChatId
                              : software
                          }}</mat-option>
                        }
                      </mat-select>
                      @if (user.get("software")?.value) {
                        <mat-icon
                          matSuffix
                          class="software-clear-icon"
                          (click)="clearSoftwareSelection($event, i)"
                          (mousedown)="
                            $event.stopPropagation(); $event.preventDefault()
                          "
                          [attr.aria-label]="translations.clear"
                          matTooltip="{{ translations.clear }}"
                        >
                          clear
                        </mat-icon>
                      }
                    </mat-form-field>
                    @if (user.get("software")?.value === "weChatId") {
                      <mat-form-field appearance="outline">
                        <mat-label>{{ translations.weChatId }}</mat-label>
                        <input
                          matInput
                          formControlName="weChatId"
                          [placeholder]="translations.weChatIdPlaceholder || ''"
                          capitalizeInput
                          (input)="onInputWithSpaceNew($event, 'weChatId', i)"
                        />
                        @if (
                          user.get("weChatId")?.hasError("required") &&
                          user.get("weChatId")?.touched
                        ) {
                          <mat-error>
                            {{ translations.wechatRequired }}
                          </mat-error>
                        }
                      </mat-form-field>
                    }
                  </div>
                </div>
              }
              <button
                mat-raised-button
                type="button"
                (click)="addUser()"
                class="user-add-btn"
              >
                {{ translations.addUserButton }}
              </button>
            </div>
            <!-- Navigation Buttons -->
            <div class="button-row">
              <button
                mat-raised-button
                color="primary"
                (click)="nextStep()"
                [disabled]="!canProceedToNextStep()"
                class="nav-next-btn"
              >
                {{ translations.next }}
              </button>
            </div>
          </div>
        }
      </div>
    }

    <!-- Review Step -->

    <!-- Company Details Review && Customer Details Review -->
    @if (step === FormStep.Review) {
      <div class="review-section">
        <!-- Company Details Review -->
        <!-- Customer Details Review -->
        <div class="review-block">
          <h3>{{ translations.companyDetails }}</h3>
          <div class="company-review-item company-block">
            <div class="company-details">
              <p>
                <strong class="review-label">{{
                  translations.companyName
                }}</strong>
                <span class="capitalize-first">{{
                  customerForm.get("name")?.value
                }}</span>
              </p>
              <p>
                <strong class="review-label">{{
                  translations.billingEmail
                }}</strong>
                {{ customerForm.get("billingEmail")?.value }}
              </p>
              <p>
                <strong class="review-label">{{ translations.taxId }}</strong>
                <span class="uppercase">{{
                  customerForm.get("taxId")?.value
                }}</span>
              </p>
              <p>
                <strong class="review-label">{{
                  translations.taxIdType
                }}</strong>
                <span>{{ customerForm.get("taxIdType")?.value }}</span>
              </p>
              <p>
                <strong class="review-label">{{
                  translations.language
                }}</strong>
                <span class="capitalize-first">{{
                  customerForm.get("language")?.value
                }}</span>
              </p>
              <p>
                <strong class="review-label">{{
                  translations.businessNumber
                }}</strong>
                <!--This is displayed as Business Phone Number-->
                {{ customerForm.get("businessNumber")?.value }}
              </p>
              <p>
                <strong class="review-label">{{ translations.country }}</strong>
                <span class="capitalize-first">{{
                  customerForm.get("country")?.value
                }}</span>
              </p>
              <p>
                <strong class="review-label">{{ translations.state }}</strong>
                <span class="capitalize-first">{{
                  customerForm.get("state")?.value?.name
                }}</span>
              </p>
              <p>
                <strong class="review-label">{{ translations.street }}</strong>
                <span class="capitalize-first">{{
                  customerForm.get("streetAddress")?.value
                }}</span>
              </p>
              <p>
                <strong class="review-label">{{ translations.suburb }}</strong>
                <span class="capitalize-first">{{
                  customerForm.get("suburb")?.value
                }}</span>
              </p>
              @if (
                customerForm.get("country")?.value === "India" ||
                customerForm.get("country")?.value === "China"
              ) {
                <p>
                  <strong class="review-label">{{ translations.city }}</strong>
                  <span class="capitalize-first">{{
                    customerForm.get("city")?.value || "N/A"
                  }}</span>
                </p>
              }
              <p>
                <strong class="review-label">{{
                  translations.postcode
                }}</strong>
                {{ customerForm.get("postCode")?.value }}
              </p>
            </div>
            <button
              class="edit-btn"
              matTooltip="{{ translations.edit }}"
              (click)="editStep(FormStep.CompanyDetails)"
            >
              <mat-icon>edit</mat-icon>
            </button>
          </div>
        </div>
        <!-- User Accounts Review -->
        <div class="review-block">
          <h3>{{ translations.userDetails }}</h3>
          <div
            [ngClass]="{
              'review-user': usersArray.length === 0,
              'review-content review-group': usersArray.length !== 0,
            }"
          >
            @if (usersArray.length === 0) {
              <p class="no-data-message">No user accounts added.</p>
            }
            @if (usersArray.length > 0) {
              <div>
                @for (user of usersArray.controls; track user; let i = $index) {
                  <div class="user-review-item user-block">
                    <h4 class="user-title">
                      {{ translations.userTitle }} {{ i + 1 }}
                    </h4>
                    <div class="user-details">
                      <p>
                        <strong class="review-label">{{
                          translations.username
                        }}</strong>
                        <span class="capitalize-first">{{
                          user.get("username")?.value
                        }}</span>
                      </p>
                      <p>
                        <strong class="review-label">{{
                          translations.firstName
                        }}</strong>
                        <span class="capitalize-first">{{
                          user.get("firstName")?.value
                        }}</span>
                      </p>
                      <p>
                        <strong class="review-label">{{
                          translations.lastName
                        }}</strong>
                        <span class="capitalize-first">{{
                          user.get("lastName")?.value
                        }}</span>
                      </p>
                      <p>
                        <strong class="review-label">{{
                          translations.email
                        }}</strong>
                        {{ user.get("email")?.value }}
                      </p>
                      <p>
                        <strong class="review-label">{{
                          translations.mobileNumber
                        }}</strong>
                        {{ user.get("mobile")?.value }}
                      </p>
                      <p>
                        <strong class="review-label">{{
                          translations.language
                        }}</strong>
                        <span class="capitalize-first">{{
                          user.get("language")?.value
                        }}</span>
                      </p>
                      <p>
                        <strong class="review-label">{{
                          translations.weChatId
                        }}</strong>
                        <span>
                          {{ user.get("software")?.value || "N/A" }} -
                          {{ user.get("weChatId")?.value || "N/A" }}
                        </span>
                      </p>
                    </div>
                    <button
                      class="edit-btn"
                      matTooltip="{{ translations.edit }}"
                      (click)="editStep(FormStep.UserAccounts)"
                    >
                      <mat-icon>edit</mat-icon>
                    </button>
                  </div>
                }
              </div>
            }
          </div>
        </div>
        <!-- Review Confirmation -->
        <!-- <div class="review-confirmation">
          <mat-checkbox formControlName="reviewConfirmed" class="review-checkbox">
            {{ translations.confirmText }}
          </mat-checkbox>
        </div> -->
        <!-- Terms & Conditions -->
        <br />
        <br />
        <div class="terms-link">
          <p>
            <span class="readonly-text">
              {{ translations.beforeProceeding }}
            </span>
            &nbsp;
            <span class="link-text" (click)="openTermsAndConditions()">
              {{ translations.termsLink }} </span
            >.
          </p>
        </div>
        <!-- <div class="button-row">
          <button mat-raised-button color="primary" (click)="onSubmit()" class="submit-btn"
            [disabled]="!customerForm.get('reviewConfirmed')?.value || customerForm.get('agreedBy')?.invalid || !isReadChecked"
            [ngClass]="{'disabled-button':!customerForm.get('reviewConfirmed')?.value || customerForm.get('agreedBy')?.invalid}" >
            {{ translations.submit}}
          </button>
        </div> -->
      </div>
    }
    @if (formSubmitted) {
      <div class="redirect-message">
        <img
          src="assets/thank-you.png"
          alt="Thank You"
          class="thank-you-image"
        />
        <p>Thank you for submitting! Redirecting to BPTFL</p>
      </div>
    }
  </form>
</mat-card>
